import { CommonModule } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import {
  ApexChart,
  ApexNonAxisChartSeries,
  ApexOptions,
  ApexPlotOptions,
  NgApexchartsModule,
} from "ng-apexcharts";
import { MatIconModule } from "@angular/material/icon";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatSelectModule } from "@angular/material/select";
import { MatOptionModule } from "@angular/material/core";
import { ApiService } from "app/service/api.service";
import { API_ROUTE } from "app/api-routes";
import { ActivityStatusType } from "app/app-constant";
import { BarChartComponent } from "app/layout/common/bar-chart/bar-chart.component";
import { RiskSummaryChartComponent } from "./risk-summary-chart/risk-summary-chart.component";
import { forkJoin } from "rxjs";
import { DonutChartComponent } from "app/layout/common/donut-chart/donut-chart.component";
import { Router } from "@angular/router";
import { BaseComponent } from "app/layout/base/base.component";
import { MatMenuModule } from "@angular/material/menu";
import { MatTooltipModule } from "@angular/material/tooltip";

interface StatusIndicator {
  color: string;
  label: string;
  borderColor: string;
}

export type ChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  title: ApexTitleSubtitle;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis;
  plotOptions: ApexPlotOptions;
  dataLabels: ApexDataLabels;
  stroke: ApexStroke;
  colors: string[];
  fill: any;
};

@Component({
  selector: "app-dashboard",
  standalone: true,
  imports: [
    NgApexchartsModule,
    CommonModule,
    MatIconModule,
    MatFormFieldModule,
    MatSelectModule,
    MatOptionModule,
    BarChartComponent,
    RiskSummaryChartComponent,
    DonutChartComponent,
    MatMenuModule,
    MatTooltipModule,
  ],
  templateUrl: "./dashboard.component.html",
  styleUrl: "./dashboard.component.scss",
})
export class DashboardComponent extends BaseComponent implements OnInit {
  dashboardData: any = this.getDefaultDashboardData();
  chartOptions: {
    series: ApexNonAxisChartSeries;
    chart: ApexChart;
    labels: string[];
    colors: string[];
    plotOptions: ApexPlotOptions;
  };

  chartNewVsReturning: ApexOptions;
  data: any;
  issues: any[] = [
    { severity: "1 - Critical", count: 10, color: "#7F1D1D", width: "208px" },
    { severity: "2 - High", count: 12, color: "#B91C1C", width: "260px" },
    { severity: "3 - Medium", count: 13, color: "#FACC15", width: "277px" },
    { severity: "4 - Low", count: 8, color: "#16A34A", width: "169px" },
  ];
  residualRiskOverview = {
    chart: {
      animations: {
        speed: 400,
        animateGradually: {
          enabled: false,
        },
      },
      fontFamily: "inherit",
      foreColor: "inherit",
      height: "120px",
      type: "donut",
      sparkline: {
        enabled: true,
      },
    },
    colors: ["#E3000C", "#FFC300", "#4390E3", "#7CD169"],
    labels: ["High Risk", "Medium Risk", "Low Risk", "Not Applicable"], // Legend labels

    plotOptions: {
      pie: {
        customScale: 0.9,
        expandOnClick: false,
        donut: {
          size: "60%",
        },
      },
    },
    series: [25, 15, 35, 25], // Example percentages

    states: {
      hover: {
        filter: {
          type: "none",
        },
      },
      active: {
        filter: {
          type: "none",
        },
      },
    },
    tooltip: {
      enabled: true,
      fillSeriesColor: false,
      theme: "dark",
      custom: ({
        seriesIndex,
        w,
      }): string => `<div class="flex items-center h-8 min-h-8 max-h-8 px-3">
                                            <div class="w-3 h-3 rounded-full" style="background-color: ${w.config.colors[seriesIndex]};"></div>
                                            <div class="ml-2 text-md leading-none">${w.config.labels[seriesIndex]}:</div>
                                            <div class="ml-2 text-md font-bold leading-none">${w.config.series[seriesIndex]}%</div>
                                        </div>`,
    },
  };

  riskAssessmentStatus = {
    chart: {
      animations: {
        speed: 400,
        animateGradually: {
          enabled: false,
        },
      },
      fontFamily: "inherit",
      foreColor: "inherit",
      height: "190px",
      type: "donut",
      sparkline: {
        enabled: true,
      },
    },
    colors: ["#E3000C", "#FFC300", "#4390E3", "#7CD169"],
    labels: ["High Risk", "Medium Risk", "Low Risk", "Not Applicable"], // Legend labels

    plotOptions: {
      pie: {
        customScale: 0.9,
        expandOnClick: false,
        donut: {
          size: "60%",
        },
      },
    },
    series: [25, 15, 35, 25], // Example percentages

    states: {
      hover: {
        filter: {
          type: "none",
        },
      },
      active: {
        filter: {
          type: "none",
        },
      },
    },
    tooltip: {
      enabled: true,
      fillSeriesColor: false,
      theme: "dark",
      custom: ({
        seriesIndex,
        w,
      }): string => `<div class="flex items-center h-8 min-h-8 max-h-8 px-3">
                                            <div class="w-3 h-3 rounded-full" style="background-color: ${w.config.colors[seriesIndex]};"></div>
                                            <div class="ml-2 text-md leading-none">${w.config.labels[seriesIndex]}:</div>
                                            <div class="ml-2 text-md font-bold leading-none">${w.config.series[seriesIndex]}%</div>
                                        </div>`,
    },
  };

  vendorCountCategory: any;
  openActivityStatusType: ActivityStatusType = ActivityStatusType.Assessment;

  riskSummary: any[] = [];
  riskSummaryType: ActivityStatusType = ActivityStatusType.Assessment;

  riskStatus: any[] = [];

  evidenceOverview: any;
  evidenceOverviewType: ActivityStatusType = ActivityStatusType.Assessment;
  pastDueDateOverviewType: ActivityStatusType = ActivityStatusType.Assessment;

  recentActivities: any[] = [];
  recentActivityType: ActivityStatusType = ActivityStatusType.Assessment;
  riskStatusType: ActivityStatusType = ActivityStatusType.Assessment;
  riskResponseOverviewType: ActivityStatusType = ActivityStatusType.Assessment;
  inherentRiskOverviewType: ActivityStatusType = ActivityStatusType.Assessment;
  // Overview
  riskResultSummaryData: any[] = [];
  residualRiskOverviewData: any[] = [];
  inherentRiskOverviewData: any[] = [];
  evidenceOverviewData: any[] = [];
  pastDueDateOverviewData: any[] = [];

  vendorCountType: string = "Category";

  constructor(
    private _apieService: ApiService,
    private _router: Router
  ) {
    super();
  }
  ngOnInit(): void {
    this._initData();
    this.setupKeyboardNavigation();
    this.chartNewVsReturning = {
      chart: {
        type: "donut",
        height: "350px",
      },
      colors: ["#FF5733", "#33FF57", "#3357FF", "#F3FF33", "#33FFF3"],
      labels: ["Item A", "Item B", "Item C", "Item D", "Item E"], // Labels for internal use only
      plotOptions: {
        pie: {
          donut: {
            size: "65%", // Adjust the size of the donut's inner circle
          },
        },
      },
      series: [30, 20, 15, 25, 10], // Values for each segment
      states: {
        hover: {
          filter: {
            type: "darken",
            value: 0.75,
          } as any,
        },
      },
      tooltip: {
        enabled: true,
        theme: "dark",
        y: {
          formatter: function (value) {
            return `${value}%`; // Append percentage to the values
          },
        },
      },
    };

    this.chartOptions = {
      series: [25, 15, 35, 25], // Example percentages
      chart: {
        type: "donut",
        height: 250, // Adjust height as needed
      },
      labels: ["High Risk", "Medium Risk", "Low Risk", "Not Applicable"], // Legend labels
      // colors: ["#E3000C", "#FFC300", "#4390E3", "#7CD169"], // Slice colors
      colors: ["#37647D", "#CE634B", "#418B7C", "#DDBA6A"], // Slice colors
      plotOptions: {
        pie: {
          donut: {
            size: "55%", // Adjust donut hole size
            labels: {
              show: true,
              name: {
                show: false,
              },
              value: {
                show: false,
              },
            },
          },
        },
      },
    };
  }

  private setupKeyboardNavigation(): void {
    document.addEventListener("keydown", (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
        const bars = document.querySelectorAll('[role="graphics-symbol"]');
        const currentFocusIndex = Array.from(bars).findIndex(
          (bar) => document.activeElement === bar
        );

        let newIndex = currentFocusIndex;
        if (event.key === "ArrowLeft") {
          newIndex =
            currentFocusIndex > 0 ? currentFocusIndex - 1 : bars.length - 1;
        } else {
          newIndex =
            currentFocusIndex < bars.length - 1 ? currentFocusIndex + 1 : 0;
        }

        (bars[newIndex] as HTMLElement).focus();
        event.preventDefault();
      }
    });
  }

  onOpenAssessementStatusChange(event: any) {
    this.openActivityStatusType = event.value;
  }

  onRecentActivitiesChange(event: any) {
    this.fetchRecentActivities(event.value);
  }

  onriskStatusChange(event: any) {
    this.riskStatusType = event.value;
    this.fetchRiskStatus(event.value);
  }

  onRiskResponseOverviewChange(event: any) {
    this.riskResponseOverviewType = event.value;
    this.fetchRiskResponseOverview(event.value);
  }

  onInherentRiskOverviewChange(event: any) {
    this.inherentRiskOverviewType = event.value;
    this.fetchInherentRiskOverview(event.value);
  }

  onEvidenceOverviewChange(event: any) {
    this.evidenceOverviewType = event.value;
    this.fetchEvidenceOverview(event.value);
  }

  onPastDueDateOverviewChange(event: any) {
    this.pastDueDateOverviewType = event.value;
    this.fetchPastDueDateOverview(event.value);
  }

  onOpenRiskSummaryChange(e: any) {
    this.riskSummaryType = e.value;
    this.fetchRiskSummary(e.value);
  }
  onVendorCountTypeChange(e: any) {
    this.vendorCountType = e.value;
  }
  private _initData() {
    this.dashboardData = this.getDefaultDashboardData();
    this._apieService.get(API_ROUTE.DASHBOARD.root).subscribe({
      next: (res: any) => {
        this.dashboardData = this.normalizeDashboardData(res?.data);
      },
      error: (error) => {
        console.error("Failed to fetch dashboard data", error);
        this.dashboardData = this.getDefaultDashboardData();
      },
    });
    this.fetchRiskSummary(this.riskSummaryType);
    this.fetchRecentActivities(this.recentActivityType);
    this.fetchRiskStatus(this.riskStatusType);
    this.fetchRiskResponseOverview(this.riskResponseOverviewType);
    this.fetchInherentRiskOverview(this.inherentRiskOverviewType);
    this.fetchEvidenceOverview(this.evidenceOverviewType);
    this.fetchPastDueDateOverview(this.pastDueDateOverviewType);
    this.fetchOverviews();
  }

  fetchRiskSummary(tag: any) {
    this._apieService.get(API_ROUTE.DASHBOARD.getRiskSummary(tag)).subscribe({
      next: (res: any) => {
        this.riskSummary = this.withFallbackData(
          this.getChartData(res?.data),
          this.getRiskSummaryFallback()
        );
      },
      error: (error) => {
        console.error("Failed to fetch risk summary", error);
        this.riskSummary = this.getRiskSummaryFallback();
      },
    });
  }

  fetchRiskStatus(tag: any) {
    this._apieService.get(API_ROUTE.DASHBOARD.getRiskStatus(tag)).subscribe({
      next: (res: any) => {
        this.riskStatus = this.withFallbackData(
          this.getChartData(res?.data),
          this.getRiskStatusFallback()
        );
      },
      error: (error) => {
        console.error("Failed to fetch risk status", error);
        this.riskStatus = this.getRiskStatusFallback();
      },
    });
  }

  fetchRecentActivities(tag: string) {
    this._apieService.get(API_ROUTE.DASHBOARD.getRecentActivities(tag)).subscribe({
      next: (res: any) => {
        this.recentActivities = Array.isArray(res?.data) ? res.data : [];
      },
      error: (error) => {
        console.error("Failed to fetch recent activities", error);
        this.recentActivities = [];
      },
    });
  }

  fetchRiskResponseOverview(tag: string) {
    this._apieService.get(API_ROUTE.DASHBOARD.getRiskResultSummary(tag)).subscribe({
      next: (res: any) => {
        this.riskResultSummaryData = this.withFallbackData(
          this.getChartData(res?.data),
          this.getRiskResultFallback()
        );
      },
      error: (error) => {
        console.error("Failed to fetch risk result overview", error);
        this.riskResultSummaryData = this.getRiskResultFallback();
      },
    });
  }

  fetchInherentRiskOverview(tag: string) {
    this._apieService.get(API_ROUTE.DASHBOARD.getInherentRiskOverview(tag)).subscribe({
      next: (res: any) => {
        this.inherentRiskOverviewData = this.withFallbackData(
          this.getChartData(res?.data),
          this.getRiskBandFallback()
        );
      },
      error: (error) => {
        console.error("Failed to fetch inherent risk overview", error);
        this.inherentRiskOverviewData = this.getRiskBandFallback();
      },
    });
  }

  fetchEvidenceOverview(tag: string) {
    this._apieService.get(API_ROUTE.DASHBOARD.getEvidenceOverview(tag)).subscribe({
      next: (res: any) => {
        this.evidenceOverviewData = this.getChartData(res?.data);
      },
      error: (error) => {
        console.error("Failed to fetch evidence overview", error);
        this.evidenceOverviewData = [];
      },
    });
  }

  fetchPastDueDateOverview(tag: string) {
    this._apieService.get(API_ROUTE.DASHBOARD.getPastDueDateOverview(tag)).subscribe({
      next: (res: any) => {
        this.pastDueDateOverviewData = this.getChartData(res?.data);
      },
      error: (error) => {
        console.error("Failed to fetch past due date overview", error);
        this.pastDueDateOverviewData = [];
      },
    });
  }

  fetchOverviews(): void {
    const apis = {
      residualRiskOverview: this._apieService.get(
        API_ROUTE.DASHBOARD.getResidualRiskOverView
      ),
    };

    forkJoin(apis).subscribe({
      next: ({ residualRiskOverview }) => {
        this.residualRiskOverviewData = this.withFallbackData(
          this.getChartData((residualRiskOverview as any)?.data),
          this.getResidualRiskFallback()
        );
      },
      error: (error) => {
        console.error("Failed to fetch dashboard overviews", error);
        this.residualRiskOverviewData = this.getResidualRiskFallback();
      },
    });
  }

  private getDefaultDashboardData(): any {
    return {
      vendorLifecycleOverview: {
        totalActiveVendors: 18,
        totalVendors: 24,
        dueDateCount: 5,
        statusCounts: {
          "in-progress": 8,
          active: 18,
          inactive: 4,
          offboarded: 2,
        },
      },
      businessRiskOverviewData: [
        { label: "High Risk", count: 4 },
        { label: "Below Average Risk", count: 5 },
        { label: "Average Risk", count: 8 },
        { label: "Moderate Risk", count: 9 },
        { label: "Low Risk", count: 6 },
      ],
      assessmentStatus: [
        { label: "Not Started", count: 4 },
        { label: "In Progress", count: 9 },
        { label: "Under Evaluation", count: 6 },
        { label: "Under Review", count: 7 },
        { label: "Completed", count: 12 },
      ],
      riskTieringStatus: [
        { label: "Not Started", count: 3 },
        { label: "In Progress", count: 7 },
        { label: "Under Evaluation", count: 4 },
        { label: "Under Review", count: 5 },
        { label: "Completed", count: 10 },
      ],
      vendorCategoryCounts: [
        { label: "Technology", count: 9 },
        { label: "Operations", count: 6 },
        { label: "Finance", count: 4 },
        { label: "Facilities", count: 3 },
      ],
      departmentCounts: [
        { label: "IT", count: 8 },
        { label: "Procurement", count: 6 },
        { label: "Risk", count: 5 },
        { label: "Legal", count: 3 },
      ],
    };
  }

  private normalizeDashboardData(data: any): any {
    const defaults = this.getDefaultDashboardData();
    const lifecycle = data?.vendorLifecycleOverview || {};
    return {
      ...defaults,
      ...(data || {}),
      vendorLifecycleOverview: {
        ...defaults.vendorLifecycleOverview,
        ...lifecycle,
        totalActiveVendors: this.toCount(
          lifecycle.totalActiveVendors ?? lifecycle.totalVendors
        ),
        totalVendors: this.toCount(lifecycle.totalVendors),
        dueDateCount: this.toCount(lifecycle.dueDateCount),
        statusCounts: {
          ...defaults.vendorLifecycleOverview.statusCounts,
          ...(lifecycle.statusCounts || {}),
        },
      },
      businessRiskOverviewData: this.withFallbackData(
        this.getChartData(data?.businessRiskOverviewData),
        defaults.businessRiskOverviewData
      ),
      assessmentStatus: this.withFallbackData(
        this.getChartData(data?.assessmentStatus),
        defaults.assessmentStatus
      ),
      riskTieringStatus: this.withFallbackData(
        this.getChartData(data?.riskTieringStatus),
        defaults.riskTieringStatus
      ),
      vendorCategoryCounts: this.withFallbackData(
        this.getChartData(data?.vendorCategoryCounts),
        defaults.vendorCategoryCounts
      ),
      departmentCounts: this.withFallbackData(
        this.getChartData(data?.departmentCounts),
        defaults.departmentCounts
      ),
    };
  }

  toCount(value: any): number {
    const count = Number(value);
    return Number.isFinite(count) ? count : 0;
  }

  getChartData(data: any): any[] {
    if (!Array.isArray(data)) return [];
    return data.map((item) => ({
      ...item,
      label: item?.label ?? "Unknown",
      count: this.toCount(item?.count),
    }));
  }

  private withFallbackData(data: any[], fallback: any[]): any[] {
    const normalized = this.getChartData(data);
    if (!normalized.length || normalized.every((item) => this.toCount(item?.count) === 0)) {
      return this.getChartData(fallback);
    }
    return normalized;
  }

  private getRiskBandFallback(): any[] {
    return [
      { label: "High", count: 5 },
      { label: "Medium", count: 11 },
      { label: "Low", count: 8 },
    ];
  }

  private getResidualRiskFallback(): any[] {
    return [
      { label: "High", count: 2 },
      { label: "Medium", count: 7 },
      { label: "Low", count: 12 },
    ];
  }

  private getRiskStatusFallback(): any[] {
    return [
      { label: "Open", count: 14 },
      { label: "Closed", count: 22 },
    ];
  }

  private getRiskSummaryFallback(): any[] {
    return [
      { label: "Critical", count: 3 },
      { label: "High", count: 7 },
      { label: "Medium", count: 12 },
      { label: "Low", count: 8 },
    ];
  }

  private getRiskResultFallback(): any[] {
    return [
      { label: "Reduced", count: 9 },
      { label: "Avoided", count: 3 },
      { label: "Ignored", count: 2 },
      { label: "Accepted", count: 6 },
      { label: "Transferred", count: 4 },
    ];
  }

  getLifecycleStatusCount(status: string): number {
    return this.toCount(
      this.dashboardData?.vendorLifecycleOverview?.statusCounts?.[status]
    );
  }

  getLifecycleTotal(): number {
    return this.toCount(
      this.dashboardData?.vendorLifecycleOverview?.totalActiveVendors ??
        this.dashboardData?.vendorLifecycleOverview?.totalVendors
    );
  }

  getRiskOverviewCount(label: string): number {
    const item = this.getChartData(
      this.dashboardData?.businessRiskOverviewData
    ).find((e: any) => e.label?.toLowerCase() === label?.toLowerCase());
    return this.toCount(item?.count);
  }

  getStatusClass(status: string): string {
    switch (status?.toLowerCase()) {
      case "initiated":
        return "status-initiated"; // Red
      case "in progress":
        return "status-in-progress"; // Yellow
      case "completed":
        return "status-completed"; // Green
      case "archived":
        return "status-archived"; // Grey
      case "not started":
        return "status-not-started"; // Red
      case "under evaluation":
        return "status-under-evaluation"; // Orange
      case "under review":
        return "status-under-review"; // Blue
      case "rejected":
        return "status-rejected"; // Blue
      default:
        return ""; // No specific styling
    }
  }

  onOpenActStatusBarClick(event: any, url: string) {
    const status = event.label === "In Progress" ? "In-progress" : event.label;
    this._router.navigate(["/" + url], { queryParams: { status } });
  }

  onRiskStatusBarClick(event: any) {
    console.log(event.label);
    let riskType;
    const riskStatus = event.label;
    if (this.riskStatusType === "assessment") riskType = "Assessment";
    else riskType = "Risk Tiering";
    this._router.navigate(["/risk-summary"], {
      queryParams: { riskType, riskStatus },
    });
  }

  onVendorCountByCatClick(event: any) {
    if (!event?.label) return;
    const queryParams =
      this.vendorCountType === "Department"
        ? { department: event.label }
        : { category: event.label };
    this._router.navigate(["/vendor-inventory"], { queryParams });
  }

  onVendorCountByDepClick(event: any) {
    if (!event?.label) return;
    this._router.navigate(["/vendor-inventory"], {
      queryParams: { department: event.label },
    });
  }

  onRiskSummaryClick(event: any) {
    let riskType;
    const riskLevel = event.label;
    if (this.riskSummaryType === "assessment") riskType = "Assessment";
    else riskType = "Risk Tiering";
    this._router.navigate(["/risk-summary"], {
      queryParams: { riskType, riskLevel, riskStatus: "Open" },
    });
  }

  onRiskResOverviewClick(event: any) {
    let riskType, riskResult;
    riskResult = event.label;
    setTimeout(() => {
      if (this.riskResponseOverviewType === "assessment")
        riskType = "Assessment";
      else riskType = "Risk Tiering";

      this._router.navigate(["/risk-summary"], {
        queryParams: { riskType, riskResult },
      });
    }, 100);
  }

  onRiskRiskOverviewChange(event: any) {
    let residualRiskBand;
    residualRiskBand = event.label;
    this._router.navigate(["/assessment"], {
      queryParams: { residualRiskBand },
    });
  }

  onVendorInventoryRedirect(status: string) {
    this._router.navigate(["/vendor-inventory"], {
      queryParams: { status },
    });
  }

  onBusinessRiskRedirect(status: string) {
    this._router.navigate(["/business-risk-dashboard"], {
      queryParams: { status },
    });
  }

  onInherentOverviewChange(event: any) {
    let inherentRiskBand;
    inherentRiskBand = event.label;
    const base =
      this.inherentRiskOverviewType === "assessment"
        ? "/assessment"
        : "/risk-tiering";

    this._router.navigate([base], {
      queryParams: { inherentRiskBand },
    });
  }

  redirectRecentActivity(val: any) {
    if (!val?._id) return;
    const id = val.assessmentId || val.rttId || "";
    const base = id.startsWith("R-") ? "risk-tiering" : "assessment";
    this._router.navigate([`${base}/form/${val._id}`]);
  }

  isAllZero(data: any[]): boolean {
    const normalizedData = this.getChartData(data);
    if (!normalizedData.length) return true;
    return normalizedData.every((item) => this.toCount(item?.count) === 0);
  }
}
